import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";

interface Testimonial {
  id: string;
  name: string;
  location: string | null;
  rating: number | null;
  content: string;
}

const emptyForm = {
  name: "",
  location: "",
  rating: 5,
  content: "",
};

export default function TestimonialsManager() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Testimonial | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("testimonials")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (err) throw err;
      setTestimonials((data as Testimonial[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (t: Testimonial) => {
    setEditing(t);
    setForm({
      name: t.name,
      location: t.location || "",
      rating: t.rating ?? 5,
      content: t.content,
    });
    setModalOpen(true);
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      name: form.name.trim(),
      location: form.location.trim() || null,
      rating: form.rating,
      content: form.content.trim(),
    };

    const { error: saveError } = editing
      ? await supabase.from("testimonials").update(payload).eq("id", editing.id)
      : await supabase.from("testimonials").insert(payload);

    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setSuccess(editing ? "Testimonial updated." : "Testimonial added.");
    setModalOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const { error: delError } = await supabase
      .from("testimonials")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", deleting.id);
    setDeleting(null);
    if (delError) {
      setError(delError.message);
      return;
    }
    setSuccess("Testimonial removed.");
    load();
  };

  const setField = (key: keyof typeof emptyForm, value: string | number) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">
            Testimonials
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            {testimonials.length} testimonials shown on the public site.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center justify-center gap-2 rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Add Testimonial
        </button>
      </div>

      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError("")} className="text-red-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {success && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <span className="text-sm text-green-700">{success}</span>
          <button onClick={() => setSuccess("")} className="text-green-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          {testimonials.map((t) => (
            <div key={t.id} className="rounded-lg border border-neutral-200 bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-dark text-brand-gold font-heading text-sm font-semibold uppercase">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="font-medium text-brand-dark">{t.name}</p>
                    <p className="text-xs text-neutral-500">{t.location || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-brand-gold">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <i key={n} className={n <= (t.rating ?? 5) ? "ri-star-fill" : "ri-star-line"}></i>
                  ))}
                </div>
              </div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                &quot;{t.content}&quot;
              </p>
              <div className="mt-4 flex justify-end gap-2 border-t border-neutral-100 pt-3">
                <button
                  onClick={() => openEdit(t)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                  title="Edit"
                >
                  <i className="ri-pencil-line"></i>
                </button>
                <button
                  onClick={() => setDeleting(t)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-red-500 hover:bg-red-50"
                  title="Delete"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
          {testimonials.length === 0 && (
            <div className="md:col-span-2 rounded-lg border border-dashed border-neutral-300 bg-white py-16 text-center text-neutral-400">
              No testimonials yet. Add your first one.
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-lg rounded-lg bg-white">
            <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
              <h3 className="font-heading text-lg font-semibold uppercase text-brand-dark">
                {editing ? "Edit Testimonial" : "Add Testimonial"}
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-neutral-400 hover:text-brand-dark">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 px-6 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-brand-dark">Name *</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="e.g. Michael T."
                    className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark">Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => setField("location", e.target.value)}
                    placeholder="e.g. Sydney, NSW"
                    className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-dark">Rating</label>
                <select
                  value={form.rating}
                  onChange={(e) => setField("rating", Number(e.target.value))}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n} star{n > 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-dark">Review *</label>
                <textarea
                  required
                  value={form.content}
                  onChange={(e) => setField("content", e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="What did the customer say?"
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
                <p className="mt-1 text-xs text-neutral-400">{form.content.length}/500</p>
              </div>

              <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 rounded-md bg-brand-gold px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
                >
                  {saving && <i className="ri-loader-4-line animate-spin"></i>}
                  {saving ? "Saving…" : "Save Testimonial"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">
              Delete testimonial?
            </h3>
            <p className="mt-2 text-sm text-neutral-500">
              This review from {deleting.name} will be moved to Trash — you can restore it later.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}